export type SavePriority = 'normal' | 'critical';

export interface SaveSchedulerOptions {
  /** Persist the current state. Called at most once at a time. */
  write: () => Promise<void>;
  debounceMs?: number;
  criticalMs?: number;
  onError?: (error: unknown) => void;
  now?: () => number;
  setTimeout?: (cb: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

/**
 * Coalesces saves (content-schemas §11.2): normal changes (placing items, camera) are debounced,
 * critical ones (purchases, claims) flush within ~300 ms, and `flush()` forces a write (tab hidden, pagehide).
 * Writes never overlap; changes made during a write trigger exactly one follow-up write.
 */
export class SaveScheduler {
  private timer: unknown;
  private timerDue = Infinity;
  private timerCritical = false;
  private dirty = false;
  private closed = false;
  private inFlight: Promise<void> | undefined;
  private readonly debounceMs: number;
  private readonly criticalMs: number;
  private readonly setTimeoutFn: (cb: () => void, ms: number) => unknown;
  private readonly clearTimeoutFn: (handle: unknown) => void;
  private readonly now: () => number;

  constructor(private readonly opts: SaveSchedulerOptions) {
    this.debounceMs = opts.debounceMs ?? 1500;
    this.criticalMs = opts.criticalMs ?? 300;
    this.now = opts.now ?? (() => performance.now());
    this.setTimeoutFn = opts.setTimeout ?? ((cb, ms) => globalThis.setTimeout(cb, ms));
    this.clearTimeoutFn =
      opts.clearTimeout ??
      ((h) => {
        globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>);
      });
  }

  get pending(): boolean {
    return this.dirty || this.inFlight !== undefined;
  }

  markDirty(priority: SavePriority = 'normal'): void {
    if (this.closed) return;
    this.dirty = true;
    const delay = priority === 'critical' ? this.criticalMs : this.debounceMs;
    const due = this.now() + delay;
    if (this.timer !== undefined) {
      // A pending critical save is never postponed; normal saves are debounced.
      if (this.timerCritical || (priority === 'critical' && due >= this.timerDue)) return;
      this.clearTimeoutFn(this.timer);
    }
    this.timerDue = due;
    this.timerCritical = priority === 'critical';
    this.timer = this.setTimeoutFn(() => {
      this.timer = undefined;
      this.timerDue = Infinity;
      this.timerCritical = false;
      void this.flush();
    }, delay);
  }

  /** Write now if anything is dirty; resolves once everything marked so far is on disk. */
  async flush(): Promise<void> {
    if (this.timer !== undefined) {
      this.clearTimeoutFn(this.timer);
      this.timer = undefined;
      this.timerDue = Infinity;
      this.timerCritical = false;
    }
    while (this.inFlight) await this.inFlight;
    if (!this.dirty || this.closed) return;
    this.dirty = false;
    this.inFlight = this.opts.write().catch((error: unknown) => {
      this.dirty = true; // retry with the next change or flush
      this.opts.onError?.(error);
    });
    try {
      await this.inFlight;
    } finally {
      this.inFlight = undefined;
    }
  }

  /** Stop for good (e.g. before a reset): nothing marked or flushed afterwards is written. */
  dispose(): void {
    this.closed = true;
    if (this.timer !== undefined) this.clearTimeoutFn(this.timer);
    this.timer = undefined;
  }
}
